import { useEffect, useState } from 'react'

import { AccountInformationsARApiProps } from 'services/account-info/types'
import { LanguagesEnum } from 'common/enums/languages'
import { normalizeLanguage } from 'utils'
import { useNavigate } from 'react-router-dom'
import { useAppInfo } from 'hooks/useAppInfo'

import IcShare from '@interco/icons/core/action-navigation/ic_share'
import IcCopy from '@interco/icons/core/action-navigation/ic_copy'
import IcEdit from '@interco/icons/core/action-navigation/ic_edit'
import ChevronRight from '@interco/icons/build-v4/orangeds/MD/chevron-right'
import CheckFill from '@interco/icons/build-v4/orangeds/MD/check-fill'
import { FlexColumn, FlexRow } from '@interco/inter-ui/components/Flex'
import { Text } from '@interco/inter-toranja'
import { BottomSheet as BottomSheetUI } from '@interco/inter-ui/components/BottomSheet'
import { interWbSession } from '@interco/inter-webview-bridge'
import {
  TextSize,
  TextType,
  TextWeight,
} from '@interco/inter-toranja/dist/components/Atoms/Text/types'

import { data as languageData } from './data'
import { getAccountIcon } from './utils/account-icons'
import { handleShareClick } from './utils/clipboard-utils'

interface CardARProps {
  item?: AccountInformationsARApiProps
  language: string
  account: string
  onAliasClick?: () => void
}

export const CardAR = ({ item, language, onAliasClick, account }: CardARProps) => {
  const normalizedLanguage = normalizeLanguage(language, LanguagesEnum.ES_ES)
  const [copied, setCopied] = useState<string>('')
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState<boolean>(false)

  const appInfo = useAppInfo()

  const navigate = useNavigate()

  const { type, title } = item ?? {}

  useEffect(() => {
    setTimeout(() => {
      copied && setCopied('')
    }, 2000)
  }, [copied])

  const handleCopyClick = (copyTitle: string, data: string) => {
    interWbSession.copyToClipboard(data, data)
    setCopied(copyTitle)

    interWbSession.requestAnalytics('DADOS_BANCARIOS_GA_FLUXO', {
      ref_figma: '66',
      flow: 'dados-bancarios',
      screen: 'dados-bancarios',
      ref_type: 'input',
      content_action: 'touch',
      action_id: 'copy',
      copy: copyTitle,
    })
  }

  const handleAliasClick = () => {
    if (onAliasClick) {
      onAliasClick()
    }

    setIsBottomSheetOpen(true)
  }

  const handleEditAliasClick = () => {
    setIsBottomSheetOpen(false)

    const currentAlias = item?.alias ?? ''
    navigate(
      `/edit-alias?alias=${encodeURIComponent(
        currentAlias,
      )}&language=${language}&account=${account}&cbuCvu=${item?.cbuCvu ?? ''}`,
    )

    interWbSession.requestAnalytics('DADOS_BANCARIOS_GA_FLUXO', {
      ref_figma: '67',
      flow: 'dados-bancarios',
      screen: 'dados-bancarios',
      ref_type: 'button',
      content_action: 'touch',
      action_id: 'edit_alias',
    })
  }

  const handleCopyAliasClick = () => {
    const aliasValue = item?.alias?.trim() ?? ''
    handleCopyClick('alias', aliasValue)

    setIsBottomSheetOpen(false)

    interWbSession.requestAnalytics('DADOS_BANCARIOS_GA_FLUXO', {
      flow: 'dados-bancarios',
      screen: 'dados-bancarios',
      ref_type: 'input',
      content_action: 'touch',
      action_id: 'copy',
      copy: 'alias',
    })
  }

  return (
    <>
      <FlexColumn gap="12px">
        <FlexRow gap="16px" justifyContent="space-between" alignItems="center">
          <FlexRow gap="16px" alignItems="center" justifyContent="flex-start">
            {getAccountIcon(type)}

            <Text textSize={TextSize.Small} as="h3" textType={TextType.Title}>
              {title}
            </Text>
          </FlexRow>

          <IcShare
            width={24}
            height={24}
            color="#1E1A19"
            onClick={() => handleShareClick(normalizedLanguage, item, title)}
          />
        </FlexRow>

        {item &&
          ['accountNumber', 'alias', 'cbuCvu', 'entity']
            .filter((key) => item[key as keyof typeof item])
            .map((key) => {
              const fieldKey = key as 'accountNumber' | 'alias' | 'cbuCvu' | 'entity'
              const fieldValue = item[fieldKey] ?? ''

              const fieldLabel =
                languageData[normalizedLanguage][
                  fieldKey as keyof typeof languageData[typeof normalizedLanguage]
                ]

              const isAlias = fieldKey === 'alias'
              const isCbuCvu = fieldKey === 'cbuCvu'
              const isAccountNumber = fieldKey === 'accountNumber'
              const isGA = type === 'ga-ar'

              const showCopyIcon = isAccountNumber || isCbuCvu || (isAlias && isGA)

              return (
                <FlexRow
                  key={fieldKey}
                  alignItems="center"
                  justifyContent="space-between"
                  gap="16px"
                  style={{ padding: '8px 0px' }}
                >
                  <FlexColumn gap="2px" style={{ flex: 1 }}>
                    <Text
                      textSize={TextSize.Medium}
                      textWeight={TextWeight.Regular}
                      textType={TextType.Label}
                    >
                      {fieldLabel}
                    </Text>

                    <Text
                      textSize={TextSize.Medium}
                      textWeight={TextWeight.Bold}
                      textType={TextType.Body}
                    >
                      {fieldValue}
                    </Text>
                  </FlexColumn>

                  {isAlias && !isGA && (
                    <ChevronRight
                      width={24}
                      height={24}
                      color="#943D15"
                      onClick={handleAliasClick}
                      style={{ flexShrink: 0 }}
                    />
                  )}

                  {showCopyIcon && (
                    <>
                      {copied === fieldKey ? (
                        <CheckFill
                          width={24}
                          height={24}
                          color="#943D15"
                          style={{ flexShrink: 0 }}
                        />
                      ) : (
                        <IcCopy
                          width={24}
                          height={24}
                          color="#943D15"
                          onClick={() => handleCopyClick(fieldKey, fieldValue)}
                          style={{ flexShrink: 0 }}
                        />
                      )}
                    </>
                  )}
                </FlexRow>
              )
            })}
      </FlexColumn>

      {isBottomSheetOpen && (
        <BottomSheetUI onClose={() => setIsBottomSheetOpen(false)} noSwipe fixedHeight="32%">
          <FlexColumn gap="0px" style={{ padding: '16px 0px' }}>
            <FlexRow
              alignItems="flex-start"
              gap="16px"
              style={{ padding: '16px 0px', cursor: 'pointer' }}
              onClick={handleEditAliasClick}
            >
              <IcEdit
                width={24}
                height={24}
                color={appInfo.isDarkMode ? '#FFFFFF' : '#1E1A19'}
                style={{ flexShrink: 0 }}
              />

              <FlexColumn gap="4px">
                <Text
                  textSize={TextSize.Medium}
                  textWeight={TextWeight.Bold}
                  textType={TextType.Body}
                >
                  {languageData[normalizedLanguage].editAlias}
                </Text>
                <Text
                  textSize={TextSize.Small}
                  textWeight={TextWeight.Regular}
                  textType={TextType.Label}
                >
                  {languageData[normalizedLanguage].editAliasDescription}
                </Text>
              </FlexColumn>
            </FlexRow>

            <FlexRow
              alignItems="flex-start"
              gap="16px"
              style={{ padding: '16px 0px', cursor: 'pointer' }}
              onClick={handleCopyAliasClick}
            >
              <IcCopy
                width={24}
                height={24}
                color={appInfo.isDarkMode ? '#FFFFFF' : '#1E1A19'}
                style={{ flexShrink: 0 }}
              />

              <FlexColumn gap="4px">
                <Text
                  textSize={TextSize.Medium}
                  textWeight={TextWeight.Bold}
                  textType={TextType.Body}
                >
                  {languageData[normalizedLanguage].copyAlias}
                </Text>

                <Text
                  textSize={TextSize.Small}
                  textWeight={TextWeight.Regular}
                  textType={TextType.Label}
                >
                  {languageData[normalizedLanguage].copyAliasDescription}
                </Text>
              </FlexColumn>
            </FlexRow>
          </FlexColumn>
        </BottomSheetUI>
      )}
    </>
  )
}
